class CollectionFabricSamples extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    this.container = document.querySelector(".product-page-fabric-samples");
    if (!this.container) {
      console.warn("Product page fabric samples container not found");
      return;
    }

    this.productsContainer =
      this.container.querySelector(".content-container__subcategories") ||
      this.container.querySelector(".fabric-samples-body");

    this.filterTypeMap = {
      "filter.p.m.fabric_info.fabric_samples_color": "colors",
      "filter.p.m.fabric_info.fabric_samples_material_type": "materials",
      "filter.p.m.fabric_info.fabric_samples_features": "features",
      "filter.p.m.fabric_info.fabric_samples_pricing_tier": "pricing",
      "filter.p.m.fabric_info.fabric_samples_fabric_name": "names",
    };
    this.referenceProducts = new Map();
    this.referenceCounts = {};
    this.currentCounts = {};
    this.checkboxSelector =
      '.fabric-samples-filter-content input[type="checkbox"]';
    this.modal = null;
    this.modalContent = null;
    this.modalTitle = null;
    this.currentModalBlockId = null;

    this.loadFabricSamplesData();
    this.setMobileFiltersSortListeners();
    this.setSortEventListeners();
    this.setFiltrationBarListeners();
    this.initializeFabricDetails();
    this.initModalSetup();
    this.detailsToggle();
    this.initMobileDrawerToggle();
    this.initializeSliders();
    this.initFilterCounting();
    this.updateResultCount();
  }

  extractImageSrcFromHtml(htmlString) {
    try {
      const div = document.createElement("div");
      div.innerHTML = htmlString;
      const img = div.querySelector("img");
      return img ? img.src || img.getAttribute("src") || "" : "";
    } catch (e) {
      console.warn("Failed to extract image src:", e);
      return "";
    }
  }

  loadFabricSamplesData() {
    // Check if fabric samples data is available in the window object
    if (window.fabricSamplesData) {
      this.fabricSamplesData = window.fabricSamplesData;
    } else {
      this.fabricSamplesData = {};

      // Try to extract data from product cards if possible
      const fabricCards = document.querySelectorAll(".fabric-samples-card");
      if (fabricCards.length) {
        const blockId =
          fabricCards[0].closest("[data-block]")?.dataset.block ||
          "fabric-block";
        this.fabricSamplesData[blockId] = [];

        fabricCards.forEach((card) => {
          const productScript = card.querySelector("script");
          if (productScript) {
            try {
              const productData = JSON.parse(productScript.innerHTML);
              // Ensure image URL is a valid string
              const featuredImage = productData.featured_image || "";
              // Validate and clean the URL if necessary
              const cleanedImage = featuredImage.startsWith("<img")
                ? this.extractImageSrcFromHtml(featuredImage)
                : featuredImage;

              this.fabricSamplesData[blockId].push({
                id: productData.id,
                title: productData.title,
                image: cleanedImage,
              });
            } catch (e) {
              console.warn("Failed to parse product data:", e);
            }
          }
        });
      }
    }
  }

  initFilterCounting() {
    this.buildReferenceData();
    if (Object.keys(this.referenceCounts).length > 0) {
      this.currentCounts = { ...this.referenceCounts };
      this.injectCounts(this.currentCounts);
      document.querySelectorAll(this.checkboxSelector).forEach((checkbox) => {
        checkbox.addEventListener("change", () => this.updateCounts());
        checkbox.addEventListener("keydown", (e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            checkbox.checked = !checkbox.checked;
            checkbox.dispatchEvent(new Event("change", { bubbles: true }));
          }
        });
      });
    }
  }

  buildReferenceData() {
    const products = Array.from(
      document.querySelectorAll(".fabric-samples-card:not(.most-popular-card)"),
    );
    if (!products.length) return;

    products.forEach((product, index) => {
      const colors = product.getAttribute("data-fabric-colors");
      const materials = product.getAttribute("data-fabric-materials");
      const features = product.getAttribute("data-fabric-features");
      const pricing = product.getAttribute("data-fabric-pricing");
      const names = product.getAttribute("data-fabric-names");

      if (!colors && !materials && !features && !pricing && !names) return;

      this.referenceProducts.set(index, {
        colors: colors
          ? colors
              .split(",")
              .map((v) => v.trim())
              .filter(Boolean)
          : [],
        materials: materials
          ? materials
              .split(",")
              .map((v) => v.trim())
              .filter(Boolean)
          : [],
        features: features
          ? features
              .split(",")
              .map((v) => v.trim())
              .filter(Boolean)
          : [],
        pricing: pricing
          ? pricing
              .split(",")
              .map((v) => v.trim())
              .filter(Boolean)
          : [],
        names: names
          ? names
              .split(",")
              .map((v) => v.trim())
              .filter(Boolean)
          : [],
      });
    });

    if (this.referenceProducts.size === 0) return;

    Object.values(this.filterTypeMap).forEach((type) => {
      this.referenceCounts[type] = {};
      const values = new Set();
      this.referenceProducts.forEach((product) => {
        product[type].forEach((v) => values.add(v.trim()));
      });
      values.forEach((value) => {
        if (!value) return;
        this.referenceCounts[type][value] = Array.from(
          this.referenceProducts.values(),
        ).filter((p) => p[type].some((v) => v.trim() === value.trim())).length;
      });
    });
  }

  getCheckedFilters() {
    const checked = {};
    document
      .querySelectorAll(this.checkboxSelector + ":checked")
      .forEach((box) => {
        const type = this.getFilterType(box);
        if (!type) return;
        if (!checked[type]) checked[type] = [];
        checked[type].push(box.dataset.name?.trim());
      });
    return checked;
  }

  updateCounts() {
    const checkedFilters = this.getCheckedFilters();
    this.currentCounts = this.calculateNewCounts(checkedFilters);
    this.injectCounts(this.currentCounts);
  }

  calculateNewCounts(checkedFilters) {
    const newCounts = {};

    document.querySelectorAll(this.checkboxSelector).forEach((checkbox) => {
      const value = checkbox.dataset.name;
      if (!value) return;

      const type = this.getFilterType(checkbox);
      if (!type) return;

      if (!newCounts[type]) newCounts[type] = {};

      if (Object.keys(checkedFilters).length === 0) {
        newCounts[type][value] = this.referenceCounts[type][value] || 0;
        return;
      }

      let matchingProducts = Array.from(this.referenceProducts.values()).filter(
        (product) => {
          if (!product[type].includes(value)) return false;

          return Object.entries(checkedFilters).every(
            ([filterType, selectedValues]) => {
              if (filterType === type && type !== "features") return true;

              if (filterType === "features") {
                return selectedValues.every((v) =>
                  product[filterType].includes(v),
                );
              } else {
                return selectedValues.some((v) =>
                  product[filterType].includes(v),
                );
              }
            },
          );
        },
      );

      newCounts[type][value] = matchingProducts.length;
    });

    return newCounts;
  }

  getFilterType(checkbox) {
    const filterGroup = checkbox.closest(".fabric-samples-filter-values");
    if (!filterGroup) return null;

    const fullFilterName = filterGroup.dataset.filterName;
    return this.filterTypeMap[fullFilterName];
  }

  injectCounts(countsObject) {
    document.querySelectorAll(this.checkboxSelector).forEach((checkbox) => {
      const value = checkbox.dataset.name;
      if (!value) return;

      const type = this.getFilterType(checkbox);
      if (!type) return;

      const count = countsObject[type]?.[value] ?? 0;
      const countElement = checkbox
        .closest(".fabric-samples-filter-value")
        ?.querySelector(".fabric-samples-filter-count");

      if (countElement) {
        countElement.textContent = `(${count})`;
      }
    });
  }

  detailsToggle() {
    let detailsElements = document.querySelectorAll(
      ".fabric-samples-filter-details",
    );
    detailsElements.forEach((details) => {
      details.addEventListener("toggle", () => {
        if (details.open && window.innerWidth > 980) {
          detailsElements.forEach((otherDetails) => {
            if (otherDetails !== details) {
              otherDetails.open = false;
            }
          });
        }
      });
    });
  }

  closeAllDetails() {
    let detailsElements = document.querySelectorAll(
      ".fabric-samples-filter-details",
    );
    detailsElements.forEach((details) => {
      if (details.open) {
        details.open = false;
      }
    });
  }

  initMobileDrawerToggle() {
    const cartContainer = document.querySelector(".fabric-samples-cart");
    if (!cartContainer) return;

    const cartHeader = cartContainer.querySelector(
      ".fabric-samples-cart-header",
    );
    if (!cartHeader) return;

    let isDrawerOpen = false;

    const toggleDrawer = (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (isDrawerOpen) {
        cartContainer.style.transform = "";
      } else {
        cartContainer.style.transform = "translateY(0)";
      }
      isDrawerOpen = !isDrawerOpen;
    };

    cartHeader.addEventListener("click", toggleDrawer);
    cartHeader.addEventListener("touchstart", toggleDrawer);
  }

  toggleMobileMenu(menuSelector) {
    const menu = document.querySelector(menuSelector);
    if (!menu) return;

    if (menu.classList.contains("is-visible")) {
      this.hideMenu(menu);
    } else {
      this.showMenu(menu);
    }
  }

  hideMenu(menu) {
    document.body.style.overflow = "";
    document.body.style.overscrollBehavior = "";
    menu.classList.remove("is-visible");
  }

  showMenu(menu) {
    document.body.style.overflow = "hidden";
    document.body.style.overscrollBehavior = "none";
    menu.classList.add("is-visible");
  }

  handleResize() {
    const filterMenu = document.querySelector("#swatch_select_head");

    if (!filterMenu) return;

    if (window.innerWidth > 980) {
      document.body.style.overflow = "";
      document.body.style.overscrollBehavior = "";
      filterMenu.classList.remove("is-visible");
    }
  }

  toggleMobileFiltersMenu() {
    this.toggleMobileMenu("#swatch_select_head");
  }

  setFiltrationBarListeners() {
    const applyFilterButtons = document.querySelectorAll(
      ".fabric-samples-filter-apply",
    );
    applyFilterButtons.forEach((button) => {
      button.addEventListener("click", (event) => {
        this.toggleFilterState(event);
        if (window.innerWidth < 981) {
          this.toggleMobileFiltersMenu();
        }
      });
    });

    const clearFiltersButtons = document.querySelectorAll(
      ".fabric-samples-filter-reset",
    );
    clearFiltersButtons.forEach((button) => {
      button.addEventListener("click", (event) => {
        this.clearFilters(event);
        if (window.innerWidth < 981) {
          this.toggleMobileFiltersMenu();
        }
      });
    });

    const applySortButton = document.querySelector(
      ".fabric-samples-sort-apply",
    );
    applySortButton.addEventListener("click", (event) => {
      this.toggleFilterState(event);
      if (window.innerWidth < 981) {
        this.toggleMobileSortMenu();
      }
    });

    const clearSortButton = document.querySelector(
      ".fabric-samples-sort-reset",
    );
    clearSortButton.addEventListener("click", (event) => {
      this.clearMobileSort(event);
      if (window.innerWidth < 981) {
        this.toggleMobileSortMenu();
      }
    });
  }

  clearFilters(event) {
    const filterContent = event.target.closest(
      ".fabric-samples-filter-content",
    );
    if (!filterContent) return;

    filterContent
      .querySelectorAll('input[type="checkbox"]')
      .forEach((checkbox) => {
        checkbox.checked = false;
      });

    this.toggleFilterState(event);
    this.updateCounts();
  }

  toggleFilterState(event) {
    event.preventDefault();
    this.linkParams = new URLSearchParams();

    const sortSelect =
      window.innerWidth > 980
        ? document.querySelector(".fabric-samples-sort-select")
        : document.querySelector(
            '.fabric-sort-option input[name="sort"]:checked',
          );
    const selectedSort = sortSelect ? sortSelect.value : "default";

    if (selectedSort !== "default") {
      this.linkParams.set("sort_by", selectedSort);
    }

    this.fetchNewResults();
  }

  setMobileFiltersSortListeners() {
    const buttons = [
      {
        selector: ".fabric-samples-mobile-filter-icon",
        action: this.toggleMobileFiltersMenu.bind(this),
      },
      {
        selector: ".swatch_select_head_mobile_close",
        action: this.toggleMobileFiltersMenu.bind(this),
      },
      {
        selector: ".fabric-samples-mobile-sort",
        action: this.toggleMobileSortMenu.bind(this),
      },
      {
        selector: ".swatch_sort_head_mobile_close",
        action: this.toggleMobileSortMenu.bind(this),
      },
    ];

    buttons.forEach(({ selector, action }) => {
      const button = document.querySelector(selector);
      if (button) button.addEventListener("click", action);
    });

    window.addEventListener("resize", this.handleResize.bind(this));
  }

  async fetchNewResults() {
    if (!this.productsContainer) return;
    const requestToken = this.filterRequestToken = (this.filterRequestToken || 0) + 1;
    this.closeAllDetails();
    await new Promise((resolve) => requestAnimationFrame(() => setTimeout(resolve, 0)));
    if (requestToken !== this.filterRequestToken) return;
    window.FabricSamplesRenderer.applyFilters(this.getCheckedFilters(), this.linkParams.get("sort_by") || "default");
    const pills = this.container.querySelector(".fabric-samples-selected-filters");
    if (pills) {
      pills.replaceChildren();
      this.container.querySelectorAll(this.checkboxSelector + ":checked").forEach((checkbox) => {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "fabric-samples-filter-remove";
        button.textContent = checkbox.dataset.name;
        // Same close icon the server-rendered pills use (snippets/enavi-icons.liquid 'close').
        button.insertAdjacentHTML(
          "beforeend",
          '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#202020" style="height:16px; width:16px" aria-hidden="true"><path d="M0 0h24v24H0z" fill="none"/><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>',
        );
        button.addEventListener("click", (event) => {
          checkbox.checked = false;
          this.toggleFilterState(event);
          this.updateCounts();
        });
        pills.appendChild(button);
      });
      if (pills.childElementCount) {
        const clear = document.createElement("button");
        clear.type = "button";
        clear.className = "fabric-samples-filter-clear-all";
        clear.textContent = "CLEAR ALL FILTERS";
        clear.addEventListener("click", (event) => this.clearAllFilters(event));
        pills.appendChild(clear);
      }
    }
    this.updateResultCount();
    document.dispatchEvent(new CustomEvent("pdp-fabric-samples:updated"));
  }

  updateResultCount() {
    const resultsElement = document.querySelector(".fabric-samples-results");
    if (resultsElement && this.productsContainer) {
      resultsElement.classList.remove("is-loading");
      const cardCount = window.FabricSamplesRenderer.getVisibleCount();
      resultsElement.innerHTML = `${cardCount} Results`;
    }
  }

  initializeFabricDetails() {
    const fabricSelector = document.querySelector(
      '.product-selector-box[data-selector-type="Fabric"]',
    );
    const viewDetailsBtn = fabricSelector?.querySelector(
      ".pdp-fabric-view-details",
    );

    if (viewDetailsBtn) {
      viewDetailsBtn.classList.add("hidden");

      viewDetailsBtn.addEventListener("click", () => {
        const activeFabric = fabricSelector.querySelector(".enavi_addon.active");
        if (!activeFabric) return;

        let targetCard = activeFabric;
        if (activeFabric.classList.contains("most-popular-card")) {
          const regularCard = fabricSelector.querySelector(
            '.enavi_addon[data-product-id="' +
              activeFabric.dataset.productId +
              '"]:not(.most-popular-card)',
          );
          if (regularCard) targetCard = regularCard;
        }

        const wrapper = targetCard.querySelector(".fabric-samples-wrapper");
        const productIdStr = wrapper?.dataset.productIdTrigger;
        const blockId = targetCard.closest("[data-block]")?.dataset.block;
        if (productIdStr && blockId) {
          this.openProductModal(parseInt(productIdStr, 10), blockId, viewDetailsBtn);
        }
      });

      const handleSelection = (e) => {
        // Clicking the collapsed selection summary row should not toggle the button.
        if (e && e.target.closest(".pdp-fabric-selection-container")) return;
        const isSelected = !!fabricSelector.querySelector(
          ".enavi_addon.active:not(.fabric-samples-decide-later)",
        );
        viewDetailsBtn.classList.toggle("hidden", !isSelected);
      };

      handleSelection();
      fabricSelector.addEventListener("click", handleSelection);
      fabricSelector.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") handleSelection(e);
      });
    }
  }

  clearAllFilters(event) {
    document
      .querySelectorAll('.fabric-samples-filter-value input[type="checkbox"]')
      .forEach((checkbox) => {
        checkbox.checked = false;
      });

    this.toggleFilterState(event);
    this.updateCounts();
  }

  initModalSetup() {
    this.modal = document.getElementById("fabric-samples-modal");
    this.modalContent = this.modal?.querySelector(".fabric-samples-modal-content");
    this.modalTitle = this.modal?.querySelector(".fabric-samples-modal-title");

    if (!this.modal) return;

    const nativeHide = this.modal.hide.bind(this.modal);
    this.modal.hide = () => {
      this.destroyModalSwiper();
      nativeHide();
    };

    this.modal.addEventListener("click", (e) => {
      const switchTarget = e.target.closest("[data-switch-product-id]");
      if (switchTarget) {
        const pid = parseInt(switchTarget.dataset.switchProductId);
        const bid = switchTarget.dataset.switchBlockId;
        if (pid && bid) this.switchModalProduct(pid, bid);
      }
    });

    this.modal.addEventListener("keydown", (e) => {
      const switchTarget = e.target.closest("[data-switch-product-id]");
      if (switchTarget && (e.key === "Enter" || e.key === " ")) {
        e.preventDefault();
        const pid = parseInt(switchTarget.dataset.switchProductId);
        const bid = switchTarget.dataset.switchBlockId;
        if (pid && bid) this.switchModalProduct(pid, bid);
      }
    });
  }

  openProductModal(productId, blockId, opener) {
    if (!this.modal || !this.modalContent) return;

    const product = window.FabricSamplesRenderer.getProductById(productId);
    const html = window.FabricSamplesRenderer.buildSingleModalHtml(productId, blockId);
    if (!html) return;

    this.currentModalBlockId = blockId;
    this.modalContent.innerHTML = html;

    if (this.modalTitle && product) {
      this.modalTitle.textContent = product.title;
    }

    if (!this.modal.hasAttribute("open")) {
      this.modal.show(opener);
    }

    this.initModalContent();
  }

  switchModalProduct(productId, blockId) {
    if (!this.modal || !this.modalContent) return;

    const product = window.FabricSamplesRenderer.getProductById(productId);
    if (!product) return;

    this.currentModalBlockId = blockId;
    this.updateModalDetails(product);
  }

  updateModalDetails(product) {
    const contentEl = this.modalContent;

    if (this.modalTitle) this.modalTitle.textContent = product.title;

    const img = contentEl.querySelector(".fabric-samples-image-container img");
    if (img) {
      img.src = product.imageLarge;
      img.alt = product.imageAlt || product.title;
      img.width = product.imageWidth;
      img.height = product.imageHeight;
    }

    const container = contentEl.querySelector(".fabric-samples-image-container");
    if (container) {
      container.classList.remove("is-zoomed");
      if (img) img.style.transform = "scale(1) translate(0, 0)";
    }

    const featureHtml = window.FabricSamplesRenderer.renderFeatureIcons(product.features || []);
    contentEl.querySelectorAll(".fabric-samples-types-popup").forEach((el) => {
      el.innerHTML = featureHtml;
    });

    window.FabricSamplesRenderer.loadModalDescription(contentEl, product);

    const fabricName = (product.fabricNames || [])[0] || "";
    const navText = contentEl.querySelector(".fabric-samples-details-navigation-text");
    if (navText) navText.textContent = `Other colors in the ${fabricName} collection:`;

    contentEl.querySelectorAll(".sample-navigation-image").forEach((el) => {
      const id = parseInt(el.dataset.switchProductId);
      el.classList.toggle("selected-navigation-image", id === product.id);
    });

    const buttonsContainer = contentEl.querySelector(".quick-buy__buttons");
    if (buttonsContainer) {
      buttonsContainer.dataset.productId = product.variantId;
      const atcBtn = buttonsContainer.querySelector(".atc-button");
      if (atcBtn) {
        const isOutOfStock = product.outOfStock;
        atcBtn.classList.toggle("disabled", isOutOfStock);
        atcBtn.disabled = isOutOfStock;
        const addSpan = atcBtn.querySelector(".atc-button__add");
        if (addSpan) {
          addSpan.textContent = isOutOfStock ? "Out Of Stock" : "+ Add to cart";
        }
      }
    }
  }

  initModalContent() {
    const contentEl = this.modalContent;

    this.initializeModalSlider(contentEl);
    this.initializeModalImageZoom(contentEl);

    const buttonsContainer = contentEl.querySelector(".quick-buy__buttons");
    if (!buttonsContainer) return;
    const atcBtn = buttonsContainer.querySelector(".atc-button");
    if (!atcBtn || atcBtn.dataset.listener) return;

    const isOutOfStock =
      atcBtn.querySelector(".atc-button__add")?.textContent.trim() === "Out Of Stock";
    if (isOutOfStock) atcBtn.classList.add("disabled");

    atcBtn.setAttribute("data-listener", "true");
    atcBtn.addEventListener("click", () => {
      const variantId = buttonsContainer.dataset.productId;
      if (!variantId || atcBtn.classList.contains("disabled")) return;
      fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      })
        .then(() => {
          atcBtn.classList.add("added-to-cart");
          document.dispatchEvent(new CustomEvent("cart:refresh"));
        })
        .catch(() => {});
    });
  }

  async initializeModalSlider(contentEl) {
    await window.theme.swiperReady;
    if (typeof Swiper === "undefined" || !contentEl.isConnected) return;
    if (window.innerWidth < 981) return;

    const slider = contentEl.querySelector(".fabric-samples-details-slider.swiper");
    if (!slider) return;

    const slideCount = slider.querySelectorAll(".swiper-slide").length;
    if (slideCount <= 1) {
      slider.classList.add("swiper-no-pagination");
      const navContainer = slider.closest(".fabric-samples-details-navigation");
      if (navContainer) {
        navContainer.querySelector(".fabric-samples-details-previous")?.style.setProperty("visibility", "hidden");
        navContainer.querySelector(".fabric-samples-details-next")?.style.setProperty("visibility", "hidden");
      }
      return;
    }
    slider.classList.remove("swiper-no-pagination");

    if (slider.swiper) slider.swiper.destroy(true, true);

    const nextEl = slider.parentElement.querySelector(".fabric-samples-details-next");
    const prevEl = slider.parentElement.querySelector(".fabric-samples-details-previous");

    const navigationContainer = slider.closest(".fabric-samples-details-navigation");
    let paginationEl = null;
    if (navigationContainer) {
      let sibling = navigationContainer.nextElementSibling;
      while (sibling) {
        if (sibling.classList.contains("swiper-pagination")) {
          paginationEl = sibling;
          break;
        }
        sibling = sibling.nextElementSibling;
      }
      if (!paginationEl) {
        paginationEl = navigationContainer.parentElement.querySelector(".swiper-pagination");
      }
    }

    // Open on the page that holds the selected fabric.
    const selectedPage = slider
      .querySelector(".selected-navigation-image")
      ?.closest(".swiper-slide");
    const initialSlide = parseInt(selectedPage?.dataset.navigationIndex, 10) || 0;

    try {
      new Swiper(slider, {
        initialSlide,
        navigation: { nextEl, prevEl },
        observer: true,
        observeParents: true,
        slidesPerView: 1,
        breakpoints: {
          300: { slidesPerView: 7.2, pagination: false },
          981: {
            slidesPerView: 1,
            pagination:
              window.innerWidth >= 981 && paginationEl && paginationEl.classList.contains("swiper-pagination")
                ? { el: paginationEl, type: "fraction" }
                : false,
          },
        },
      });
    } catch (error) {
      console.error("Error initializing modal slider:", error);
    }
  }

  initializeModalImageZoom(contentEl) {
    const containers = contentEl.querySelectorAll(".fabric-samples-image-container");
    const isMobile = () => window.innerWidth <= 980;

    containers.forEach((container) => {
      const zoomIcon = container.querySelector(".fabric-samples-zoom-icon");
      const img = container.querySelector("img");
      if (!img) return;

      let isZoomed = false;
      let isDragging = false;
      let startX, startY, currentX, currentY;
      const ZOOM_SCALE = 2;
      const centerPosition = -(100 - 100 / ZOOM_SCALE) / 2;

      const toggleZoom = () => {
        isZoomed = !isZoomed;
        container.classList.toggle("is-zoomed", isZoomed);
        img.style.transform = isZoomed
          ? `scale(${ZOOM_SCALE}) translate(${centerPosition}%, ${centerPosition}%)`
          : "scale(1) translate(0, 0)";
      };

      if (zoomIcon) {
        zoomIcon.addEventListener("click", () => {
          if (!isMobile()) toggleZoom();
        });
      }

      container.addEventListener("click", (e) => {
        if (!isMobile()) return;
        toggleZoom();
        e.preventDefault();
        e.stopPropagation();
      });

      container.addEventListener("mousemove", (e) => {
        if (!isZoomed || isMobile()) return;
        const rect = container.getBoundingClientRect();
        const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
        const translateX = -(100 - 100 / ZOOM_SCALE) * x;
        const translateY = -(100 - 100 / ZOOM_SCALE) * y;
        img.style.transform = `scale(${ZOOM_SCALE}) translate(${translateX}%, ${translateY}%)`;
      });

      container.addEventListener("mouseleave", () => {
        if (!isZoomed || isMobile()) return;
        img.style.transform = `scale(${ZOOM_SCALE}) translate(${centerPosition}%, ${centerPosition}%)`;
      });

      container.addEventListener(
        "touchstart",
        (e) => {
          if (!isZoomed) return;
          isDragging = true;
          const touch = e.touches[0];
          startX = touch.clientX;
          startY = touch.clientY;
          const style = window.getComputedStyle(img);
          const matrix = new DOMMatrix(style.transform);
          currentX = (matrix.m41 / (matrix.m11 * img.offsetWidth)) * 100;
          currentY = (matrix.m42 / (matrix.m11 * img.offsetHeight)) * 100;
          e.stopPropagation();
        },
        { passive: false },
      );

      container.addEventListener(
        "touchmove",
        (e) => {
          if (!isZoomed || !isDragging) return;
          const touch = e.touches[0];
          const maxTranslate = 100 - 100 / ZOOM_SCALE;
          const newX = Math.max(
            -maxTranslate,
            Math.min(0, currentX + ((touch.clientX - startX) / container.offsetWidth) * 115),
          );
          const newY = Math.max(
            -maxTranslate,
            Math.min(0, currentY + ((touch.clientY - startY) / container.offsetHeight) * 115),
          );
          img.style.transform = `scale(${ZOOM_SCALE}) translate(${newX}%, ${newY}%)`;
          e.preventDefault();
          e.stopPropagation();
        },
        { passive: false },
      );

      container.addEventListener("touchend", () => {
        isDragging = false;
      });
    });
  }

  destroyModalSwiper() {
    if (!this.modalContent) return;
    const swiper = this.modalContent.querySelector(".swiper")?.swiper;
    if (swiper) swiper.destroy(true, true);
  }

  async initializeSliders() {
    await window.theme.swiperReady;
    if (typeof Swiper === "undefined") return;
    if (
      !this.fabricSamplesData ||
      Object.keys(this.fabricSamplesData).length === 0
    ) {
      console.warn("No fabric samples data available for sliders");
      return;
    }

    Object.entries(this.fabricSamplesData).forEach(([blockId, products]) => {
      const block = document.querySelector(`[data-block="${blockId}"]`);
      if (!block || !products || products.length === 0) return;

      const swiperWrappers = block.querySelectorAll(".swiper-wrapper");
      swiperWrappers.forEach((wrapper) => {
        const wrapperProductId = wrapper.dataset.product;
        const slidesHTML = this.generateSlidesHTML(
          products,
          wrapperProductId,
          blockId,
        );
        wrapper.innerHTML = slidesHTML;
      });
    });

    document.querySelectorAll(".swiper").forEach((slider) => {
      // Skip if Swiper is not loaded or already initialized
      if (typeof Swiper === "undefined" || slider.swiper) return;

      const nextEl = slider.parentElement.querySelector(
        ".fabric-samples-details-next",
      );
      const prevEl = slider.parentElement.querySelector(
        ".fabric-samples-details-previous",
      );
      const paginationEl =
        slider.parentElement.parentElement.querySelector(".swiper-pagination");

      new Swiper(slider, {
        navigation: {
          nextEl,
          prevEl,
        },
        slidesPerView: 1,
        breakpoints: {
          300: {
            slidesPerView: 7.2,
            pagination: false,
          },
          981: {
            slidesPerView: 1,
            pagination: {
              el: paginationEl,
              type: "fraction",
            },
          },
        },
      });
    });
  }

  generateSlidesHTML(products, wrapperProductId, blockId) {
    const isMobile = window.innerWidth < 980;
    let slideIndex = 0;

    return products.reduce((html, product, index) => {
      if (!isMobile && index % 10 === 0) {
        if (index !== 0) html += "</div>";

        if (products.length < 6) {
          html += `<div class="swiper-slide swiper-slide-row swiper-slide-custom" data-navigation-index="${slideIndex}">`;
        } else {
          html += `<div class="swiper-slide swiper-slide-row" data-navigation-index="${slideIndex}">`;
        }
        slideIndex++;
      }

      if (isMobile) {
        html += `<div class="swiper-slide swiper-slide-row" data-navigation-index="${index}">`;
      }

      const selectedNavigationImage =
        product.id == wrapperProductId ? "selected-navigation-image" : "";

      // Fix: Ensure image URL is properly escaped and formatted
      const imageUrl = product.image
        ? product.image.replace(/"/g, "&quot;")
        : "";
      const imageAlt = `${product.id}-${blockId}`.replace(/"/g, "&quot;");

      if (isMobile) {
        html += `
          <div class="sample-navigation-image ${selectedNavigationImage}">
            <img data-src="${imageUrl}" alt="${imageAlt}">
          </div>
        `;
      } else {
        html += `
          <div class="sample-navigation-image ${selectedNavigationImage}">
            <img src="${imageUrl}" alt="${imageAlt}">
          </div>
        `;
      }

      if (
        isMobile ||
        (!isMobile && index === products.length - 1 && index % 10 !== 0)
      ) {
        html += "</div>";
      }

      return html;
    }, "");
  }

  setSortEventListeners() {
    document
      .querySelector(".fabric-samples-sort-select")
      .addEventListener("change", this.toggleFilterState.bind(this));
  }

  toggleMobileSortMenu() {
    this.toggleMobileMenu("#swatch_sort_mobile_head");
  }

  clearMobileSort(event) {
    document
      .querySelectorAll('.fabric-sort-option input[type="radio"]')
      .forEach((radio) => {
        radio.checked = false;
      });
    this.toggleFilterState(event);
  }
}

document.addEventListener("pdp-fabric-samples:loaded", () => {
  if (!customElements.get("collection-fabric-samples")) {
    customElements.define("collection-fabric-samples", CollectionFabricSamples);
  }
});
